import { computed, effect, inject, Injectable, signal } from '@angular/core';
import { canAdministerSchool, School, SchoolData } from '../../models/school.model';
import {
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationSkipped,
  Router,
} from '@angular/router';
import { filter, firstValueFrom, Observable, tap } from 'rxjs';
import { HttpClient, HttpErrorResponse, httpResource } from '@angular/common/http';
import { environment } from '../../../../environments/environment';
import { AuthService } from '../../../auth/services/auth.service';
import { ApiErrorHandler } from '../../../core/errors/api-error-handler';
import { ErrorReportingService } from '../../../core/services/error-reporting-service/error-reporting-service';
import { NotificationService } from '../../../core/services/notification-service/notification-service';

const SELECTED_SCHOOL_KEY = 'SELECTED_SCHOOL_ID';

/**
 * The part of a path that decides which school's page it is. Every route under `/svps/:svpId` —
 * the shell's tabs and the profile beside it — shows the one ŠVP, so moving between them stays
 * on that ŠVP's school; anywhere else the path itself is the page.
 */
export function pageRoot(path: string): string {
  return /^\/svps\/[^/]+/.exec(path)?.[0] ?? path;
}

@Injectable({
  providedIn: 'root',
})
export class SchoolService {
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly apiErrors = inject(ApiErrorHandler);
  private readonly authService = inject(AuthService);
  private readonly errorReporting = inject(ErrorReportingService);
  private readonly notificationService = inject(NotificationService);

  #schoolsResource = httpResource<School[]>(() =>
    this.authService.isAuthenticated() ? `${environment.apiUrl}/schools` : undefined,
  );
  readonly schools = computed(() => {
    if (this.#schoolsResource.status() === 'error') {
      return [];
    }
    return this.#schoolsResource.value() ?? [];
  });
  #mutationLoading = signal(false);

  /**
   * Bumped whenever a write actually flips {@link School.isMinorityLanguageSchool}. The server
   * revalidates every study program of the school on that change, so anything holding study-program
   * data (its `errors` gain or lose `another_fl_not_introduced`) is stale and must refetch. A plain
   * rename does not touch validation and leaves this alone.
   */
  readonly #minorityLanguageVersion = signal(0);
  readonly minorityLanguageVersion = this.#minorityLanguageVersion.asReadonly();
  readonly isLoading = computed(() => this.#schoolsResource.isLoading() || this.#mutationLoading());

  /** The school the header shows and every page works with; remembered across logins. */
  #selectedSchoolId = signal<string | null>(localStorage.getItem(SELECTED_SCHOOL_KEY));
  selectedSchoolId = this.#selectedSchoolId.asReadonly();
  readonly selectedSchool = computed(() => {
    return this.schools().find((s) => s.id === this.#selectedSchoolId()) ?? null;
  });

  readonly #hasLoadedOnce = signal(false);
  readonly isInitialLoading = computed(() => !this.#hasLoadedOnce());

  #resolveLoaded!: () => void;

  /**
   * Resolves once the first `GET /schools` has settled *and* {@link selectedSchool} is the school
   * the header shows — the effect below still has to step in when the stored choice is gone, and
   * until it does the selection is `null`. For a decision that makes no sense over an empty list
   * or a half-settled selection — a route guard judging a school's license — awaited instead of
   * watching {@link isInitialLoading}: `toObservable` called from a guard hangs its effect on the
   * root injector, where `take(1)` does not tear it down and every navigation leaves another
   * behind.
   */
  readonly whenLoaded = new Promise<void>((resolve) => (this.#resolveLoaded = resolve));

  /** The one check for "may this person administer that school" — see {@link canAdministerSchool}. */
  canAdminister(school: School | null): boolean {
    return canAdministerSchool(this.authService.currentUser(), school);
  }

  constructor() {
    effect(() => {
      if (this.#schoolsResource.status() === 'error') {
        this.apiErrors.notify(this.#schoolsResource.error(), 'SchoolService.loadSchools');
      }
    });
    // Not `selectSchool`: its navigation home would throw a deep link (a bookmarked ŠVP on a
    // first login, a `returnUrl`) back to the dashboard the moment the school list arrives.
    effect(() => {
      if (!this.selectedSchoolExists() && this.schools().length > 0) {
        this.setSelectedSchool(this.schools()[0].id, { persist: true });
      }
    });
    effect(() => {
      const status = this.#schoolsResource.status();
      if (status === 'resolved' || status === 'error') {
        this.#hasLoadedOnce.set(true);
      }
    });
    // Waiting for the selection too, not just the load: with no school selectable there is nothing
    // to wait for, and re-running until one is keeps this from depending on the order the effect
    // above happens to run in.
    effect(() => {
      if (
        this.#hasLoadedOnce() &&
        (this.selectedSchool() !== null || this.schools().length === 0)
      ) {
        this.#resolveLoaded();
      }
    });
    effect(() => {
      if (!this.authService.isAuthenticated()) {
        this.clearSelectedSchool();
      }
    });
  }

  /**
   * Posts the new school and hands the caller the request, so a dialog that owns the action can
   * see it fail and keep the filled-in form open. The list refresh stays here; the error does not.
   */
  createSchool(data: SchoolData): Observable<School> {
    return this.http
      .post<School>(`${environment.apiUrl}/schools`, data)
      .pipe(tap(() => this.#schoolsResource.reload()));
  }

  /** @see createSchool — the caller owns the failure, including the 403 on a school it may not edit. */
  updateSchool(id: string, data: SchoolData): Observable<School> {
    const previous = this.schools().find((s) => s.id === id) ?? null;
    return this.http.put<School>(`${environment.apiUrl}/schools/${id}`, data).pipe(
      tap((updated: School) => {
        if (previous && previous.isMinorityLanguageSchool !== updated.isMinorityLanguageSchool) {
          this.#minorityLanguageVersion.update((v) => v + 1);
        }
        this.#schoolsResource.reload();
      }),
    );
  }

  deleteSchool(id: string) {
    this.#mutationLoading.set(true);
    this.http.delete(`${environment.apiUrl}/schools/${id}`).subscribe({
      next: (_) => {
        this.#mutationLoading.set(false);
        this.#schoolsResource.reload();
      },
      error: (error: HttpErrorResponse) => {
        this.#mutationLoading.set(false);
        this.apiErrors.notify(error, 'SchoolService.deleteSchool');
      },
    });
  }

  reloadSchools(): void {
    this.#schoolsResource.reload();
  }

  /**
   * The user picked a school in the header: the page they were on belongs to the old one, so
   * the selection moves only once that page is actually gone. An `unsavedChangesGuard` can cancel
   * the navigation home, and the page that stays open must keep being judged by its own school.
   * The navigation is also cancelled when the user clicks on before it settles — then the page
   * is left just the same, only elsewhere, so the URL decides once the router is quiet.
   *
   * On the dashboard and the school list there is nothing to leave, so the selection moves at
   * once — the router skips a navigation to the current URL and resolves it with `false`, which
   * would otherwise swallow the choice.
   */
  selectSchool(id: string): void {
    const path = this.#currentPath();
    if (path === '/' || path === '/schools') {
      this.setSelectedSchool(id, { persist: true });
      return;
    }
    this.router.navigate(['/']).then(
      async (navigated) => {
        // A guard on the way home can also have ended the session (a redirect to `/login`);
        // the choice is then not this user's to keep.
        if ((navigated || (await this.#hasLeft(path))) && this.authService.isAuthenticated()) {
          this.setSelectedSchool(id, { persist: true });
        }
      },
      (error: unknown) => {
        // A guard on the way home threw: the page is still open, so the choice cannot be applied,
        // and the user must not be left thinking it was.
        this.errorReporting.report(error, 'SchoolService.selectSchool');
        this.notificationService.showTemporaryError('Školu se nepodařilo přepnout', 'Zavřít');
      },
    );
  }

  /**
   * Whether the page that was open at `path` is gone, asked after a navigation away from it
   * resolved `false`. That answer does not say why: a guard kept the page, or another navigation
   * superseded this one — which may itself still be running, and may itself be kept back. So this
   * waits for the router to fall quiet and then reads the URL, the one fact that settles it.
   *
   * Compared by {@link pageRoot}: a superseding click on a tab of the same ŠVP changes the URL
   * but not the page, and the ŠVP still open belongs to its own school.
   */
  async #hasLeft(path: string): Promise<boolean> {
    while (this.router.getCurrentNavigation() !== null) {
      await firstValueFrom(
        this.router.events.pipe(
          filter(
            (event) =>
              event instanceof NavigationEnd ||
              event instanceof NavigationCancel ||
              event instanceof NavigationError ||
              event instanceof NavigationSkipped,
          ),
        ),
      );
    }
    return pageRoot(this.#currentPath()) !== pageRoot(path);
  }

  #currentPath(): string {
    return this.router.url.split(/[?#]/)[0];
  }

  /**
   * Makes `id` the selected school without leaving the page — for a page that already shows
   * that school's data (an open ŠVP) and only needs the header to say so. The selection is the
   * one "current school" of the application, so the ŠVP writes it rather than competing with it,
   * and it sticks: the school of the last opened ŠVP is where "Domů" and the next login land.
   */
  syncSelectedSchool(id: string): void {
    this.setSelectedSchool(id, { persist: true });
  }

  private setSelectedSchool(id: string, { persist }: { persist: boolean }): void {
    if (persist) {
      localStorage.setItem(SELECTED_SCHOOL_KEY, id);
    }
    if (this.#selectedSchoolId() === id) return;
    this.#selectedSchoolId.set(id);
  }

  clearSelectedSchool(): void {
    this.#selectedSchoolId.set(null);
    localStorage.removeItem(SELECTED_SCHOOL_KEY);
  }

  private selectedSchoolExists(): boolean {
    return (
      this.#selectedSchoolId() != null &&
      this.schools().some((s) => s.id === this.#selectedSchoolId())
    );
  }
}
